import React, { useState, useMemo, useCallback, useRef, useEffect } from 'react';
import { Search, BookOpen, Lock, Unlock, Filter, RefreshCw, Loader, BarChart3, History, Check, X, Edit3 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useGetNotesDevoirsQuery, useSaisieIndividuelleMutation as useSaisieDevoirMutation } from '../../services/notesDevoirsApi';
import { useGetNotesExamensQuery, useSaisieIndividuelleMutation as useSaisieExamenMutation } from '../../services/notesExamensApi';
import { useGetHistoriqueQuery as useGetHistoriqueDevoirsQuery } from '../../services/notesDevoirsApi';
import { useGetHistoriqueQuery as useGetHistoriqueExamensQuery } from '../../services/notesExamensApi';
import { useGetChoixFormulaireQuery, useGetEtudiantsQuery } from '../../services/etudiantsApi';
import { useGetMatieresByFiltersQuery } from '../../services/matieresApi';
import { useGetAnneeActiveQuery } from '../../services/anneeAcademiqueApi';
import { useGetClassesQuery } from '../../services/polesOptionsApi';
import ScrollToTop from '../../components/ui/ScrollToTop';
import { typeFormationForClasse } from '../../utils/typeFormation';
import { formatNote } from '../../utils/formatNote';
import BulletinModal from '../../components/Bulletin/BulletinModal';
import ActionsRapidesNotesPanel from '../../components/NotesTools/ActionsRapidesNotesPanel';
import ReporterExamenSiZeroModal from '../../components/NotesTools/ReporterExamenSiZeroModal';
import ReporterExamenSiZeroBatchModal from '../../components/NotesTools/ReporterExamenSiZeroBatchModal';
import HistoriqueNoteModal from '../../components/NotesTools/HistoriqueNoteModal';
import AuditNotesModal from '../../components/NotesTools/AuditNotesModal';
import AnalyseIntelligenteModal from '../../components/NotesTools/AnalyseIntelligenteModal';
import ModalRachatIntelligent from '../Resultats/ModalRachatIntelligent';
import { FileText, Sparkles, Clock } from 'lucide-react';

// ── Types pour les lignes fusionnées ────────────────────────────────────────

interface LigneFusionnee {
  etudiant_id: number;
  etudiant_matricule: string;
  etudiant_nom: string;
  etudiant_classe: string;
  matiere_id: number;
  matiere_code: string;
  matiere_nom: string;
  // Devoirs
  noteDevoir_id: number | null;
  d1: number | null;
  absent1: boolean;
  d2: number | null;
  absent2: boolean;
  moyenneDevoirs: number | null;
  devoirVerrouille: boolean;
  // Examens
  noteExamen_id: number | null;
  so: number | null; // Session Ordinaire
  absentSO: boolean;
  sr: number | null; // Session Rattrapage
  absentSR: boolean;
  examenVerrouille: boolean;
  // Traçabilité saisie (séparée devoirs vs examens)
  saisi_par_nom: string | null;            // legacy / fallback
  saisi_par_nom_devoir: string | null;     // qui a saisi D1/D2
  modifie_par_nom_devoir: string | null;   // dernier modifieur D1/D2
  saisi_par_nom_examen: string | null;     // qui a saisi SO/SR
  modifie_par_nom_examen: string | null;   // dernier modifieur examen
  date_modification: string;
}

type SortField = 'nom' | 'matricule' | 'moyenne' | 'd1' | 'd2' | 'so' | 'sr';

export const NotesList: React.FC = () => {
  const navigate = useNavigate();
  const { data: choixFormulaire } = useGetChoixFormulaireQuery();
  const { data: anneeActive } = useGetAnneeActiveQuery();
  const { data: allClasses = [] } = useGetClassesQuery({ actif: true });

  const [niveau, setNiveau] = useState('');
  const [classe, setClasse] = useState('');
  const [poleFilter, setPoleFilter] = useState('');
  const [matiereId, setMatiereId] = useState<number | null>(null);
  const [semestre, setSemestre] = useState<'S1' | 'S2'>('S1');

  // Verrouillage matière : conserve la matière lors du changement de classe
  // (utile pour consulter la même matière sur plusieurs classes successives).
  // On retient le NOM (pas l'id) car l'id change selon l'option de la classe.
  const [matiereLocked, setMatiereLocked] = useState(false);
  const [lockedMatiereName, setLockedMatiereName] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [sortField, setSortField] = useState<SortField>('nom');
  const [sortAsc, setSortAsc] = useState(true);
  const [avecNotesUniquement, setAvecNotesUniquement] = useState(false);

  // Inline editing
  const [editingCell, setEditingCell] = useState<{ rowKey: string; field: string } | null>(null);
  const [editValue, setEditValue] = useState('');
  const editInputRef = useRef<HTMLInputElement>(null);

  // History panel
  const [showHistorique, setShowHistorique] = useState(false);

  // Modal Bulletin (pour un étudiant sélectionné)
  const [bulletinEtudiantId, setBulletinEtudiantId] = useState<number | null>(null);
  // Modal Rachat intelligent (pour un étudiant sélectionné)
  const [rachatEtudiant, setRachatEtudiant] = useState<{
    etudiantId: number;
    matricule: string;
    nom: string;
    moyenneActuelle: number | null;
  } | null>(null);
  // Modal historique d'une ligne précise (Option A)
  const [historiqueLigne, setHistoriqueLigne] = useState<{
    noteDevoirId: number | null;
    noteExamenId: number | null;
    etudiantNom: string;
    etudiantMatricule: string;
    matiereCode: string;
    matiereNom: string;
  } | null>(null);
  // Modal audit global (Option B)
  const [showAuditModal, setShowAuditModal] = useState(false);
  // Modal report examen→devoir si devoir=0 (action sensible)
  const [showReportZeroModal, setShowReportZeroModal] = useState(false);
  // Modal report examen→devoir BATCH (toutes matières d'un scope)
  const [showReportZeroBatchModal, setShowReportZeroBatchModal] = useState(false);
  // Modal analyse intelligente (4 scénarios)
  const [analyseScenario, setAnalyseScenario] = useState<
    null | 'examen_sans_note' | 'examen_partielle' | 'devoir_sans_note' | 'devoir_partielle'
  >(null);
  // Recherche dans la sidebar matières
  const [matiereSearchSidebar, setMatiereSearchSidebar] = useState('');

  const anneeAcademique = anneeActive?.libelle || localStorage.getItem('annee_academique') || '';

  // Niveaux
  const niveaux = useMemo(() => {
    if (!choixFormulaire?.niveaux) return [];
    return choixFormulaire.niveaux.filter(n => n.count > 0).map(n => n.code);
  }, [choixFormulaire]);

  // Classes filtrées par niveau et pôle
  const classes = useMemo(() => {
    if (!niveau) return [];
    let filtered = allClasses.filter(c => c.niveau === niveau);
    if (poleFilter) {
      filtered = filtered.filter(c => c.pole_nom === poleFilter);
    }
    return filtered.map(c => ({ code: c.code, label: `${c.code} — ${c.option_nom}`, option_nom: c.option_nom }));
  }, [allClasses, niveau, poleFilter]);

  // Option de la classe sélectionnée
  const classeSelectionnee = useMemo(() => {
    if (!classe) return null;
    return allClasses.find(c => c.code === classe) || null;
  }, [allClasses, classe]);

  // Convertir S1/S2 en numéro de semestre selon le niveau (mapping LMD).
  // L1: S1=1, S2=2 | L2: S1=3, S2=4 | L3: S1=5, S2=6.
  //
  // Fallback : si `niveau` n'est pas explicitement sélectionné dans les filtres,
  // on le dérive du code de la classe (ex. `L1GRH-CS` → `L1`). Sans ce fallback
  // le semestre restait absent de la requête et changer S1↔S2 ne modifiait pas
  // la liste des matières.
  const niveauEffectif = useMemo(() => {
    if (niveau) return niveau;
    if (!classe) return '';
    for (const nv of ['L1', 'L2', 'L3', 'M1', 'M2']) {
      if (classe.toUpperCase().startsWith(nv)) return nv;
    }
    return '';
  }, [niveau, classe]);

  const semestreNum = useMemo(() => {
    if (!niveauEffectif) return undefined;
    const base = niveauEffectif === 'L1' ? 0
      : niveauEffectif === 'L2' ? 2
      : niveauEffectif === 'L3' ? 4
      : niveauEffectif === 'M1' ? 6
      : niveauEffectif === 'M2' ? 8
      : 0;
    return base + (semestre === 'S1' ? 1 : 2);
  }, [niveauEffectif, semestre]);

  // Matières filtrées STRICTEMENT par classe + semestre
  // (utilise Option.code dérivé du code de classe — évite le bug AI/AII).
  const { data: matieres } = useGetMatieresByFiltersQuery(
    { classe: classe || undefined, semestre: semestreNum },
    { skip: !classe }
  );

  const selectedMatiere = useMemo(
    () => (Array.isArray(matieres) ? matieres.find((m: any) => m.id === matiereId) : undefined),
    [matieres, matiereId]
  );

  // Si la matière est verrouillée et que la classe change, on tente de retrouver
  // une matière homonyme dans la nouvelle classe (id différent par option).
  useEffect(() => {
    if (!matiereLocked || !lockedMatiereName) return;
    if (!Array.isArray(matieres) || matieres.length === 0) return;
    const match = matieres.find((m: any) => m.nom_matiere === lockedMatiereName);
    if (match && match.id !== matiereId) {
      setMatiereId(match.id);
    } else if (!match && matiereId !== null) {
      setMatiereId(null);
    }
  }, [matieres, matiereLocked, lockedMatiereName]);

  const toggleMatiereLock = () => {
    if (matiereLocked) {
      setMatiereLocked(false);
      setLockedMatiereName(null);
    } else if (selectedMatiere) {
      setLockedMatiereName(selectedMatiere.nom_matiere);
      setMatiereLocked(true);
    }
  };

  // Étudiants de la classe (pour afficher ceux sans notes). CS si classe finit
  // par "-CS", sinon FI + DD.
  const { data: etudiantsClasse = [] } = useGetEtudiantsQuery(
    { classe, actif: true, type_formation: typeFormationForClasse(classe) },
    { skip: !classe }
  );

  // Notes devoirs
  const devoirsParams = useMemo(() => ({
    annee_academique: anneeAcademique,
    semestre,
    ...(matiereId ? { matiere_id: matiereId } : {}),
    ...(classe ? { classe } : {}),
  }), [anneeAcademique, semestre, matiereId, classe]);

  const { data: notesDevoirs, isLoading: loadingDevoirs, refetch: refetchDevoirs } = useGetNotesDevoirsQuery(
    devoirsParams,
    { skip: !anneeAcademique || !classe }
  );

  // Notes examens (SO)
  const examensSOParams = useMemo(() => ({
    annee_academique: anneeAcademique,
    semestre,
    ...(matiereId ? { matiere_id: matiereId } : {}),
    ...(classe ? { classe } : {}),
    est_rattrapage: false,
  }), [anneeAcademique, semestre, matiereId, classe]);

  const { data: notesSO, isLoading: loadingSO, refetch: refetchSO } = useGetNotesExamensQuery(
    examensSOParams,
    { skip: !anneeAcademique || !classe }
  );

  // Notes examens (SR)
  const examensSRParams = useMemo(() => ({
    annee_academique: anneeAcademique,
    semestre,
    ...(matiereId ? { matiere_id: matiereId } : {}),
    ...(classe ? { classe } : {}),
    est_rattrapage: true,
  }), [anneeAcademique, semestre, matiereId, classe]);

  const { data: notesSR, isLoading: loadingSR, refetch: refetchSR } = useGetNotesExamensQuery(
    examensSRParams,
    { skip: !anneeAcademique || !classe }
  );

  const isLoading = loadingDevoirs || loadingSO || loadingSR;

  const refetchAll = useCallback(() => {
    refetchDevoirs();
    refetchSO();
    refetchSR();
  }, [refetchDevoirs, refetchSO, refetchSR]);

  // Mutations pour l'édition inline
  const [saisirDevoir] = useSaisieDevoirMutation();
  const [saisirExamen] = useSaisieExamenMutation();

  // Historique
  const { data: historiqueDevoirsData } = useGetHistoriqueDevoirsQuery({}, { skip: !showHistorique });
  const { data: historiqueExamensData } = useGetHistoriqueExamensQuery({}, { skip: !showHistorique });

  // Fusionner les données devoirs + examens par étudiant+matière
  const lignesFusionnees: LigneFusionnee[] = useMemo(() => {
    const map = new Map<string, LigneFusionnee>();

    // Insérer les devoirs
    (notesDevoirs || []).forEach(d => {
      const key = `${d.etudiant}-${d.matiere}`;
      map.set(key, {
        etudiant_id: d.etudiant,
        etudiant_matricule: d.etudiant_matricule,
        etudiant_nom: d.etudiant_nom,
        etudiant_classe: d.etudiant_classe || '',
        matiere_id: d.matiere,
        matiere_code: d.matiere_code,
        matiere_nom: d.matiere_nom,
        noteDevoir_id: d.id,
        d1: d.note1 != null ? parseFloat(String(d.note1)) : null,
        absent1: d.absent1,
        d2: d.note2 != null ? parseFloat(String(d.note2)) : null,
        absent2: d.absent2,
        moyenneDevoirs: d.moyenne_devoirs != null ? parseFloat(String(d.moyenne_devoirs)) : null,
        devoirVerrouille: d.verrouille,
        noteExamen_id: null,
        so: null,
        absentSO: false,
        sr: null,
        absentSR: false,
        examenVerrouille: false,
        saisi_par_nom: d.saisi_par_nom,
        saisi_par_nom_devoir: d.saisi_par_nom,
        modifie_par_nom_devoir: d.modifie_par_nom,
        saisi_par_nom_examen: null,
        modifie_par_nom_examen: null,
        date_modification: d.date_modification,
      });
    });

    // Enrichir avec les examens SO
    (notesSO || []).forEach(e => {
      const key = `${e.etudiant}-${e.matiere}`;
      const existing = map.get(key);
      if (existing) {
        existing.noteExamen_id = e.id;
        existing.so = e.note_examen != null ? parseFloat(String(e.note_examen)) : null;
        existing.absentSO = e.absent;
        existing.examenVerrouille = e.verrouille;
        existing.saisi_par_nom_examen = e.saisi_par_nom;
        existing.modifie_par_nom_examen = e.modifie_par_nom;
      } else {
        map.set(key, {
          etudiant_id: e.etudiant,
          etudiant_matricule: e.etudiant_matricule,
          etudiant_nom: e.etudiant_nom,
          etudiant_classe: e.etudiant_classe || '',
          matiere_id: e.matiere,
          matiere_code: e.matiere_code,
          matiere_nom: e.matiere_nom,
          noteDevoir_id: null,
          d1: null, absent1: false,
          d2: null, absent2: false,
          moyenneDevoirs: null,
          devoirVerrouille: false,
          noteExamen_id: e.id,
          so: e.note_examen != null ? parseFloat(String(e.note_examen)) : null,
          absentSO: e.absent,
          sr: null, absentSR: false,
          examenVerrouille: e.verrouille,
          saisi_par_nom: e.saisi_par_nom,
          saisi_par_nom_devoir: null,
          modifie_par_nom_devoir: null,
          saisi_par_nom_examen: e.saisi_par_nom,
          modifie_par_nom_examen: e.modifie_par_nom,
          date_modification: e.date_modification,
        });
      }
    });

    // Enrichir avec les examens SR
    (notesSR || []).forEach(e => {
      const key = `${e.etudiant}-${e.matiere}`;
      const existing = map.get(key);
      if (existing) {
        existing.sr = e.note_examen != null ? parseFloat(String(e.note_examen)) : null;
        existing.absentSR = e.absent;
        // SR est saisi via examen aussi : on garde l'info si pas de SO
        if (!existing.saisi_par_nom_examen) {
          existing.saisi_par_nom_examen = e.saisi_par_nom;
          existing.modifie_par_nom_examen = e.modifie_par_nom;
        }
      } else {
        map.set(key, {
          etudiant_id: e.etudiant,
          etudiant_matricule: e.etudiant_matricule,
          etudiant_nom: e.etudiant_nom,
          etudiant_classe: e.etudiant_classe || '',
          matiere_id: e.matiere,
          matiere_code: e.matiere_code,
          matiere_nom: e.matiere_nom,
          noteDevoir_id: null,
          d1: null, absent1: false,
          d2: null, absent2: false,
          moyenneDevoirs: null,
          devoirVerrouille: false,
          noteExamen_id: null,
          so: null, absentSO: false,
          sr: e.note_examen != null ? parseFloat(String(e.note_examen)) : null,
          absentSR: e.absent,
          examenVerrouille: e.verrouille,
          saisi_par_nom: e.saisi_par_nom,
          saisi_par_nom_devoir: null,
          modifie_par_nom_devoir: null,
          saisi_par_nom_examen: e.saisi_par_nom,
          modifie_par_nom_examen: e.modifie_par_nom,
          date_modification: e.date_modification,
        });
      }
    });

    // Ajouter les étudiants de la classe qui n'ont aucune note (uniquement si une matière est sélectionnée)
    if (matiereId && etudiantsClasse.length > 0) {
      const matiere = matieres?.find((m: any) => m.id === matiereId);
      for (const etud of etudiantsClasse) {
        const dejaPresent = Array.from(map.values()).some(l => l.etudiant_id === etud.id);
        if (!dejaPresent) {
          const key = `${etud.id}-${matiereId}-empty`;
          map.set(key, {
            etudiant_id: etud.id,
            etudiant_matricule: etud.matricule,
            etudiant_nom: `${etud.nom} ${etud.prenoms}`,
            etudiant_classe: etud.classe || classe || '',
            matiere_id: matiereId,
            matiere_code: matiere?.code_matiere || '',
            matiere_nom: matiere?.nom_matiere || '',
            noteDevoir_id: null,
            d1: null, absent1: false,
            d2: null, absent2: false,
            moyenneDevoirs: null,
            devoirVerrouille: false,
            noteExamen_id: null,
            so: null, absentSO: false,
            sr: null, absentSR: false,
            examenVerrouille: false,
            saisi_par_nom: null,
            saisi_par_nom_devoir: null,
            modifie_par_nom_devoir: null,
            saisi_par_nom_examen: null,
            modifie_par_nom_examen: null,
            date_modification: '',
          });
        }
      }
    }

    return Array.from(map.values());
  }, [notesDevoirs, notesSO, notesSR, etudiantsClasse, matiereId, matieres]);

  // Stats
  const nbAvecNotes = useMemo(() => lignesFusionnees.filter(l =>
    l.d1 !== null || l.d2 !== null || l.so !== null || l.sr !== null
  ).length, [lignesFusionnees]);
  const nbSansNotes = lignesFusionnees.length - nbAvecNotes;

  // Filtrage + tri
  const filteredNotes = useMemo(() => {
    let result = [...lignesFusionnees];

    // Filtre "avec notes uniquement"
    if (avecNotesUniquement) {
      result = result.filter(n =>
        n.d1 !== null || n.d2 !== null || n.so !== null || n.sr !== null
      );
    }

    if (search.trim()) {
      const q = search.toLowerCase();
      result = result.filter(n =>
        n.etudiant_matricule?.toLowerCase().includes(q) ||
        n.etudiant_nom?.toLowerCase().includes(q) ||
        n.matiere_nom?.toLowerCase().includes(q) ||
        n.matiere_code?.toLowerCase().includes(q)
      );
    }

    result.sort((a, b) => {
      let cmp = 0;
      if (sortField === 'nom') cmp = (a.etudiant_nom || '').localeCompare(b.etudiant_nom || '');
      else if (sortField === 'matricule') cmp = (a.etudiant_matricule || '').localeCompare(b.etudiant_matricule || '');
      else if (sortField === 'moyenne') cmp = (a.moyenneDevoirs ?? -1) - (b.moyenneDevoirs ?? -1);
      else if (sortField === 'd1') cmp = (a.d1 ?? -1) - (b.d1 ?? -1);
      else if (sortField === 'd2') cmp = (a.d2 ?? -1) - (b.d2 ?? -1);
      else if (sortField === 'so') cmp = (a.so ?? -1) - (b.so ?? -1);
      else if (sortField === 'sr') cmp = (a.sr ?? -1) - (b.sr ?? -1);
      return sortAsc ? cmp : -cmp;
    });

    return result;
  }, [lignesFusionnees, search, sortField, sortAsc, avecNotesUniquement]);

  const handleSort = (field: SortField) => {
    if (sortField === field) setSortAsc(v => !v);
    else { setSortField(field); setSortAsc(true); }
  };

  const SortIcon = ({ field }: { field: SortField }) => {
    if (sortField !== field) return <span className="ml-1 text-gray-300 dark:text-gray-600">↕</span>;
    return <span className="ml-1 text-blue-500">{sortAsc ? '↑' : '↓'}</span>;
  };

  // Stats locales
  const localStats = useMemo(() => {
    if (!filteredNotes.length) return null;
    const avecMoy = filteredNotes.filter(n => n.moyenneDevoirs != null);
    const avgMoy = avecMoy.length ? avecMoy.reduce((acc, n) => acc + n.moyenneDevoirs!, 0) / avecMoy.length : 0;
    const avecSO = filteredNotes.filter(n => n.so != null && !n.absentSO);
    const avgSO = avecSO.length ? avecSO.reduce((acc, n) => acc + n.so!, 0) / avecSO.length : 0;
    return {
      total: filteredNotes.length,
      avecMoyenne: avecMoy.length,
      moyenneDevoirs: avgMoy,
      moyenneSO: avgSO,
      absentsD1: filteredNotes.filter(n => n.absent1).length,
      absentsD2: filteredNotes.filter(n => n.absent2).length,
      absentsSO: filteredNotes.filter(n => n.absentSO).length,
    };
  }, [filteredNotes]);

  // ── Inline editing ──────────────────────────────────────────────────────────

  const startEditing = useCallback((rowKey: string, field: string, currentValue: number | null) => {
    setEditingCell({ rowKey, field });
    setEditValue(currentValue != null ? currentValue.toString() : '');
  }, []);

  useEffect(() => {
    if (editingCell && editInputRef.current) {
      editInputRef.current.focus();
      editInputRef.current.select();
    }
  }, [editingCell]);

  const cancelEditing = useCallback(() => {
    setEditingCell(null);
    setEditValue('');
  }, []);

  const saveEdit = useCallback(async () => {
    if (!editingCell) return;
    const { rowKey, field } = editingCell;
    const ligne = lignesFusionnees.find(l => `${l.etudiant_id}-${l.matiere_id}` === rowKey);
    if (!ligne) { cancelEditing(); return; }

    const noteVal = editValue.trim() === '' ? null : parseFloat(editValue);
    if (noteVal !== null && (isNaN(noteVal) || noteVal < 0 || noteVal > 20)) {
      cancelEditing();
      return;
    }

    try {
      if (field === 'd1' || field === 'd2') {
        // IMPORTANT : on envoie TOUS les champs (note1, note2, absent1, absent2)
        // car le backend a une logique de full-update — ne pas envoyer un
        // champ ÉCRASE sa valeur en DB à null.
        const note1 = field === 'd1' ? noteVal : (ligne.d1 ?? null);
        const note2 = field === 'd2' ? noteVal : (ligne.d2 ?? null);
        await saisirDevoir({
          etudiant_id: ligne.etudiant_id,
          matiere_id: ligne.matiere_id,
          annee_academique: anneeAcademique,
          semestre,
          note1, absent1: ligne.absent1 || false,
          note2, absent2: ligne.absent2 || false,
        }).unwrap();
        refetchDevoirs();
      } else if (field === 'so') {
        await saisirExamen({
          etudiant_id: ligne.etudiant_id,
          matiere_id: ligne.matiere_id,
          annee_academique: anneeAcademique,
          semestre,
          note_examen: noteVal,
          est_rattrapage: false,
        }).unwrap();
        refetchSO();
      } else if (field === 'sr') {
        await saisirExamen({
          etudiant_id: ligne.etudiant_id,
          matiere_id: ligne.matiere_id,
          annee_academique: anneeAcademique,
          semestre,
          note_examen: noteVal,
          est_rattrapage: true,
        }).unwrap();
        refetchSR();
      }
    } catch (err) {
      console.error('Erreur modification note:', err);
    }
    cancelEditing();
  }, [editingCell, editValue, lignesFusionnees, anneeAcademique, semestre, saisirDevoir, saisirExamen, refetchDevoirs, refetchSO, refetchSR, cancelEditing]);

  const handleEditKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') saveEdit();
    else if (e.key === 'Escape') cancelEditing();
  };

  // Render cell éditable.
  // Le clic droit (onContextMenu) ouvre l'historique de modifications de la
  // ligne (idem que le bouton Clock en bout de ligne).
  const renderEditableCell = (ligne: LigneFusionnee, field: 'd1' | 'd2' | 'so' | 'sr', value: number | null, absent: boolean, verrouille: boolean) => {
    const rowKey = `${ligne.etudiant_id}-${ligne.matiere_id}`;
    const isEditing = editingCell?.rowKey === rowKey && editingCell?.field === field;

    const handleContextMenu = (e: React.MouseEvent) => {
      e.preventDefault();
      if (!ligne.noteDevoir_id && !ligne.noteExamen_id) return;
      setHistoriqueLigne({
        noteDevoirId: ligne.noteDevoir_id,
        noteExamenId: ligne.noteExamen_id,
        etudiantNom: ligne.etudiant_nom,
        etudiantMatricule: ligne.etudiant_matricule,
        matiereCode: ligne.matiere_code,
        matiereNom: ligne.matiere_nom,
      });
    };

    if (isEditing) {
      return (
        <div className="flex items-center gap-1">
          <input
            ref={editInputRef}
            type="number"
            min="0"
            max="20"
            step="0.25"
            value={editValue}
            onChange={e => setEditValue(e.target.value)}
            onKeyDown={handleEditKeyDown}
            onBlur={saveEdit}
            className="w-16 px-1 py-0.5 text-center text-sm border border-blue-400 rounded bg-white dark:bg-gray-700 dark:text-gray-100 focus:outline-none focus:ring-1 focus:ring-blue-500"
          />
        </div>
      );
    }

    if (absent) {
      return (
        <span
          className="text-amber-600 dark:text-amber-400 text-xs font-medium cursor-context-menu"
          onContextMenu={handleContextMenu}
          title="Clic droit : voir l'historique"
        >
          ABS
        </span>
      );
    }
    if (value !== null) {
      return (
        <span
          className={`${value >= 10 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'} ${!verrouille ? 'cursor-pointer hover:underline' : ''}`}
          onDoubleClick={() => !verrouille && startEditing(rowKey, field, value)}
          onContextMenu={handleContextMenu}
          title={!verrouille ? 'Double-clic : modifier · Clic droit : historique' : 'Verrouillé · Clic droit : historique'}
        >
          {formatNote(value)}
        </span>
      );
    }
    return (
      <span
        className={`text-gray-300 dark:text-gray-600 ${!verrouille ? 'cursor-pointer hover:text-gray-500' : ''}`}
        onDoubleClick={() => !verrouille && startEditing(rowKey, field, null)}
        onContextMenu={handleContextMenu}
        title={!verrouille ? 'Double-clic : saisir · Clic droit : historique' : ''}
      >
        —
      </span>
    );
  };

  // ── Historique fusionné ─────────────────────────────────────────────────────

  const historiqueFusionne = useMemo(() => {
    const items: Array<{
      id: string;
      type: 'devoir' | 'examen';
      etudiant: string;
      matiere: string;
      champ: string;
      ancien: string | null;
      nouveau: string | null;
      par: string | null;
      date: string;
    }> = [];
    (historiqueDevoirsData || []).forEach(h => {
      items.push({
        id: `d-${h.id}`,
        type: 'devoir',
        etudiant: h.etudiant_matricule,
        matiere: h.matiere_nom,
        champ: h.champ_modifie,
        ancien: h.ancienne_valeur,
        nouveau: h.nouvelle_valeur,
        par: h.modifie_par_nom,
        date: h.date_modification,
      });
    });
    (historiqueExamensData || []).forEach(h => {
      items.push({
        id: `e-${h.id}`,
        type: 'examen',
        etudiant: h.etudiant_matricule,
        matiere: h.matiere_nom,
        champ: h.champ_modifie,
        ancien: h.ancienne_valeur,
        nouveau: h.nouvelle_valeur,
        par: h.modifie_par_nom,
        date: h.date_modification,
      });
    });
    return items.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime()).slice(0, 100);
  }, [historiqueDevoirsData, historiqueExamensData]);

  // Filtrer matières sidebar
  const matieresSidebar = useMemo(() => {
    if (!Array.isArray(matieres)) return [];
    if (!matiereSearchSidebar.trim()) return matieres;
    const q = matiereSearchSidebar.toLowerCase();
    return matieres.filter((m: any) =>
      (m.nom_matiere || '').toLowerCase().includes(q) ||
      (m.code_matiere || '').toLowerCase().includes(q)
    );
  }, [matieres, matiereSearchSidebar]);

  // Navigation clavier : ↑/↓ pour parcourir la liste des matières (sidebar)
  // Désactivé si focus dans un input/textarea/select pour ne pas casser les saisies
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (!classe || matieresSidebar.length === 0) return;
      const t = e.target as HTMLElement;
      if (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.tagName === 'SELECT' || t.isContentEditable) return;
      if (e.key !== 'ArrowDown' && e.key !== 'ArrowUp') return;
      e.preventDefault();
      const idx = matieresSidebar.findIndex((m: any) => m.id === matiereId);
      let next = idx;
      if (e.key === 'ArrowDown') {
        next = idx < 0 ? 0 : Math.min(matieresSidebar.length - 1, idx + 1);
      } else if (e.key === 'ArrowUp') {
        next = idx <= 0 ? 0 : idx - 1;
      }
      const newMat = matieresSidebar[next];
      if (newMat && newMat.id !== matiereId) {
        setMatiereId(newMat.id);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [matieresSidebar, matiereId, classe]);

  // Stats : nb étudiants ayant/n'ayant pas note d'examen et de devoir.
  // Basé sur lignesFusionnees (qui contient déjà tous les étudiants de la classe
  // quand une matière est sélectionnée — cf. enrichissement avec etudiantsClasse).
  const statsNotes = useMemo(() => {
    if (!matiereId || !classe) return null;
    let avec_ex = 0, avec_dv = 0;
    for (const l of lignesFusionnees) {
      const a_ex = l.so !== null || l.absentSO || l.sr !== null || l.absentSR;
      const a_dv = l.d1 !== null || l.absent1 || l.d2 !== null || l.absent2;
      if (a_ex) avec_ex++;
      if (a_dv) avec_dv++;
    }
    const total = lignesFusionnees.length;
    return {
      total,
      avec_examen: avec_ex,
      sans_examen: total - avec_ex,
      avec_devoir: avec_dv,
      sans_devoir: total - avec_dv,
    };
  }, [lignesFusionnees, matiereId, classe]);

  return (
    <div className="max-w-screen-2xl mx-auto px-2 sm:px-4 lg:px-6 py-4 space-y-3">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Consultation des Notes</h1>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
            Vue globale : devoirs (D1, D2) et examens (SO, SR) — {anneeAcademique}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setShowHistorique(!showHistorique)}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${showHistorique ? 'bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300' : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'}`}
          >
            <History size={16} />
            Historique
          </button>
          <button
            onClick={() => navigate('/saisie-notes-devoirs')}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm font-medium"
          >
            <BookOpen size={16} />
            Saisir des Notes
          </button>
        </div>
      </div>

      {/* Filtres compacts (sticky) */}
      <div className="sticky top-0 z-20 bg-white dark:bg-gray-800 rounded-lg shadow border border-gray-200 dark:border-gray-700 px-3 py-2">
        <div className="flex items-center gap-2 flex-wrap">
          <Filter size={14} className="text-blue-500 shrink-0" />
          {/* Pôle */}
          <select value={poleFilter}
            onChange={e => { setPoleFilter(e.target.value); setClasse(''); if (!matiereLocked) setMatiereId(null); }}
            className="px-2 py-1 text-xs border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-1 focus:ring-blue-500">
            <option value="">Tous pôles</option>
            {['Polytechnique', 'Commerce', 'Juridique'].map(p => <option key={p} value={p}>{p}</option>)}
          </select>
          {/* Niveau */}
          <select value={niveau}
            onChange={e => { setNiveau(e.target.value); setClasse(''); if (!matiereLocked) setMatiereId(null); }}
            className="px-2 py-1 text-xs border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-1 focus:ring-blue-500">
            <option value="">Niveau</option>
            {niveaux.map(n => <option key={n} value={n}>{n}</option>)}
          </select>
          {/* Classe */}
          <select value={classe}
            onChange={e => { setClasse(e.target.value); if (!matiereLocked) setMatiereId(null); }}
            disabled={!niveau}
            className="px-2 py-1 text-xs font-bold border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-1 focus:ring-blue-500 disabled:bg-gray-100 dark:disabled:bg-gray-600">
            <option value="">— Classe —</option>
            {classes.map(c => <option key={c.code} value={c.code}>{c.code}</option>)}
          </select>
          {/* Verrouillage matière (conserve la matière en changeant de classe) */}
          <button
            type="button"
            onClick={toggleMatiereLock}
            disabled={!matiereLocked && !matiereId}
            title={matiereLocked
              ? `Déverrouiller (matière fixée : ${lockedMatiereName})`
              : 'Verrouiller la matière (conservée lors du changement de classe)'}
            className={`flex items-center gap-1 px-2 py-1 text-xs rounded transition-colors ${
              matiereLocked
                ? 'bg-amber-200 dark:bg-amber-700 text-amber-900 dark:text-amber-100 hover:bg-amber-300 dark:hover:bg-amber-600 font-semibold'
                : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600 disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-gray-100'
            }`}
          >
            {matiereLocked ? <Lock size={12} /> : <Unlock size={12} />}
            {matiereLocked && (
              <span className="hidden sm:inline truncate max-w-[120px]">{lockedMatiereName}</span>
            )}
          </button>
          {/* Semestre */}
          <select value={semestre}
            onChange={e => setSemestre(e.target.value as 'S1' | 'S2')}
            className="px-2 py-1 text-xs border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-1 focus:ring-blue-500">
            <option value="S1">S1</option>
            <option value="S2">S2</option>
          </select>
          <span className="text-[10px] text-gray-500 dark:text-gray-400">{anneeAcademique}</span>
          {/* Recherche */}
          <div className="relative flex-1 min-w-[180px]">
            <Search size={12} className="absolute left-2 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Matricule, nom…"
              className="w-full pl-6 pr-2 py-1 text-xs border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-1 focus:ring-blue-500"
            />
          </div>
          {matiereId && (
            <label className="flex items-center gap-1 text-[10px] cursor-pointer">
              <input type="checkbox" checked={avecNotesUniquement} onChange={(e) => setAvecNotesUniquement(e.target.checked)}
                className="rounded w-3 h-3 text-blue-600" />
              Avec notes uniquement
            </label>
          )}
          <button
            onClick={() => setShowHistorique(!showHistorique)}
            className={`px-2 py-1 text-xs rounded ${showHistorique ? 'bg-amber-100 dark:bg-amber-900/30 text-amber-700' : 'bg-gray-100 dark:bg-gray-700 text-gray-600'}`}
          >
            <History size={12} className="inline mr-1" />Historique
          </button>
        </div>
      </div>

      {/* Layout 2 colonnes : sidebar matières + zone principale */}
      <div className="grid grid-cols-1 lg:grid-cols-[240px_minmax(0,1fr)] gap-3">
        {/* SIDEBAR MATIÈRES */}
        <aside className="bg-white dark:bg-gray-800 rounded-lg shadow border border-gray-200 dark:border-gray-700 overflow-hidden self-start lg:sticky lg:top-16">
          <div className="p-2 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-700/40">
            <h3 className="text-xs font-semibold text-gray-800 dark:text-gray-200 flex items-center justify-between">
              {classe ? `Matières de ${classe}` : 'Matières'}
              <span className="text-[10px] font-normal text-gray-500 bg-gray-200 dark:bg-gray-600 px-1.5 py-0.5 rounded">
                {(matieres || []).length}
              </span>
            </h3>
            <input
              type="text"
              value={matiereSearchSidebar}
              onChange={(e) => setMatiereSearchSidebar(e.target.value)}
              placeholder="Rechercher…"
              disabled={!classe}
              className="mt-1.5 w-full px-2 py-1 text-xs border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 disabled:bg-gray-100 dark:disabled:bg-gray-600"
            />
          </div>
          <div className="max-h-[calc(100vh-180px)] overflow-y-auto">
            {!classe ? (
              <div className="p-4 text-center text-xs text-gray-400">Choisissez une classe</div>
            ) : matieresSidebar.length === 0 ? (
              <div className="p-4 text-center text-xs text-gray-400">Aucune matière</div>
            ) : (
              matieresSidebar.map((m: any) => {
                const active = m.id === matiereId;
                return (
                  <button
                    key={m.id}
                    onClick={() => setMatiereId(m.id)}
                    className={`w-full text-left px-2 py-1.5 border-b border-gray-100 dark:border-gray-700 transition-colors ${
                      active
                        ? 'bg-blue-50 dark:bg-blue-900/30 border-l-4 border-l-blue-600'
                        : 'hover:bg-gray-50 dark:hover:bg-gray-700/40 border-l-4 border-l-transparent'
                    }`}
                  >
                    <div className="font-mono text-[10px] text-gray-500 dark:text-gray-400">
                      {m.code_matiere}
                    </div>
                    <div className={`text-xs font-medium ${active ? 'text-blue-700 dark:text-blue-300' : 'text-gray-800 dark:text-gray-200'}`}>
                      {m.nom_matiere}
                    </div>
                  </button>
                );
              })
            )}
          </div>
        </aside>

        {/* ZONE PRINCIPALE */}
        <div className="space-y-3">

      {/* Actions rapides (visible uniquement si matière + classe sélectionnées) */}
      {matiereId && classe && (
        <ActionsRapidesNotesPanel
          matiereId={matiereId}
          classe={classe}
          semestre={semestre}
          anneeAcademique={anneeAcademique}
          onSuccess={refetchAll}
        />
      )}

      {/* Action sensible : reporter examen → devoir UNIQUEMENT pour ceux à 0 */}
      <div className="bg-amber-50 dark:bg-amber-900/10 border border-amber-200 dark:border-amber-800 rounded-lg p-3 flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-start gap-2 min-w-0 flex-1">
          <span className="text-amber-600 dark:text-amber-400 text-base flex-shrink-0">⚖️</span>
          <div className="min-w-0">
            <div className="text-sm font-semibold text-gray-800 dark:text-gray-200">
              Reporter examen → devoir (seulement si devoir = 0)
            </div>
            <div className="text-[11px] text-gray-600 dark:text-gray-400">
              Attribue <b>max(SO, SR)</b> au devoir des étudiants ayant 0 au devoir ET ayant composé l'examen.
              Ignore les devoirs verrouillés et tous ceux qui ont déjà une vraie note.
            </div>
          </div>
        </div>
        <div className="flex gap-2 flex-wrap">
          {matiereId && classe && (
            <button onClick={() => setShowReportZeroModal(true)}
              className="px-3 py-1.5 bg-amber-600 hover:bg-amber-700 text-white text-xs rounded font-medium whitespace-nowrap">
              Cette matière…
            </button>
          )}
          <button onClick={() => setShowReportZeroBatchModal(true)}
            className="px-3 py-1.5 bg-amber-700 hover:bg-amber-800 text-white text-xs rounded font-medium whitespace-nowrap"
            title="Appliquer à toutes les matières d'un scope (classe, pôle, niveau, option)">
            En masse (classe / pôle / niveau)…
          </button>
        </div>
      </div>

      {/* Panneau Analyse intelligente (4 scénarios bulk) + Audit */}
      <div className="bg-gradient-to-r from-amber-50 to-orange-50 dark:from-amber-900/10 dark:to-orange-900/10 rounded-lg border border-amber-200 dark:border-amber-800 p-3">
        <div className="flex items-center gap-2 mb-2">
          <span className="text-amber-600 dark:text-amber-400 text-base">⚡</span>
          <span className="text-sm font-semibold text-gray-800 dark:text-gray-200">
            Analyse intelligente — actions en masse
          </span>
          <span className="text-[10px] text-gray-500 dark:text-gray-400 italic">
            Scanne toutes les matières × classes et propose les actions correctives
          </span>
          <button
            onClick={() => setShowAuditModal(true)}
            title="Voir l'historique global des modifications de notes"
            className="ml-auto flex items-center gap-1 px-3 py-1.5 text-xs bg-indigo-100 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-300 hover:bg-indigo-200 dark:hover:bg-indigo-900/50 border border-indigo-300 dark:border-indigo-700 rounded"
          >
            <Clock size={12} /> Audit modifications
          </button>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
          <button
            onClick={() => setAnalyseScenario('examen_sans_note')}
            className="flex items-start gap-2 p-2 bg-white dark:bg-gray-800 border border-blue-200 dark:border-blue-800 rounded hover:bg-blue-50 dark:hover:bg-blue-900/20 text-left"
          >
            <span className="text-lg">🎯</span>
            <div>
              <div className="text-xs font-semibold text-blue-700 dark:text-blue-300">
                1. Examens sans aucune note → reporter Devoirs
              </div>
              <div className="text-[10px] text-gray-600 dark:text-gray-400">
                Cas TP : pas d'examen fait, on copie la moyenne devoir vers SO
              </div>
            </div>
          </button>
          <button
            onClick={() => setAnalyseScenario('examen_partielle')}
            className="flex items-start gap-2 p-2 bg-white dark:bg-gray-800 border border-cyan-200 dark:border-cyan-800 rounded hover:bg-cyan-50 dark:hover:bg-cyan-900/20 text-left"
          >
            <span className="text-lg">📋</span>
            <div>
              <div className="text-xs font-semibold text-cyan-700 dark:text-cyan-300">
                2. Examens avec cases vides → note collective
              </div>
              <div className="text-[10px] text-gray-600 dark:text-gray-400">
                Combler les manques avec une note par défaut (ex: 0)
              </div>
            </div>
          </button>
          <button
            onClick={() => setAnalyseScenario('devoir_sans_note')}
            className="flex items-start gap-2 p-2 bg-white dark:bg-gray-800 border border-emerald-200 dark:border-emerald-800 rounded hover:bg-emerald-50 dark:hover:bg-emerald-900/20 text-left"
          >
            <span className="text-lg">📚</span>
            <div>
              <div className="text-xs font-semibold text-emerald-700 dark:text-emerald-300">
                3. Devoirs sans aucune note → reporter Examen
              </div>
              <div className="text-[10px] text-gray-600 dark:text-gray-400">
                Quand le formateur n'a pas fait de devoir, on copie SO vers D1+D2
              </div>
            </div>
          </button>
          <button
            onClick={() => setAnalyseScenario('devoir_partielle')}
            className="flex items-start gap-2 p-2 bg-white dark:bg-gray-800 border border-purple-200 dark:border-purple-800 rounded hover:bg-purple-50 dark:hover:bg-purple-900/20 text-left"
          >
            <span className="text-lg">📝</span>
            <div>
              <div className="text-xs font-semibold text-purple-700 dark:text-purple-300">
                4. Devoirs avec cases vides → note collective
              </div>
              <div className="text-[10px] text-gray-600 dark:text-gray-400">
                Combler les D1/D2 manquants avec une note par défaut
              </div>
            </div>
          </button>
        </div>
      </div>

      {/* Statistiques moyennes */}
      {localStats && matiereId && (
        <div className="grid grid-cols-3 sm:grid-cols-6 gap-2">
          {[
            { label: 'Total', value: localStats.total, color: 'text-gray-900 dark:text-gray-100' },
            { label: 'Avec moy.', value: localStats.avecMoyenne, color: 'text-blue-600 dark:text-blue-400' },
            { label: 'Moy. dev', value: formatNote(localStats.moyenneDevoirs), color: localStats.moyenneDevoirs >= 10 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400' },
            { label: 'Moy. SO', value: formatNote(localStats.moyenneSO), color: localStats.moyenneSO >= 10 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400' },
            { label: 'Abs D1', value: localStats.absentsD1, color: 'text-amber-600' },
            { label: 'Abs SO', value: localStats.absentsSO, color: 'text-amber-600' },
          ].map(s => (
            <div key={s.label} className="bg-white dark:bg-gray-800 rounded border border-gray-200 dark:border-gray-700 px-2 py-1.5">
              <div className="text-[10px] text-gray-500 dark:text-gray-400">{s.label}</div>
              <div className={`text-base font-bold ${s.color}`}>{s.value}</div>
            </div>
          ))}
        </div>
      )}

      {/* Statistiques de présence (devoir / examen) */}
      {statsNotes && (
        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-3">
          <div className="text-xs font-semibold text-gray-700 dark:text-gray-300 mb-2 flex items-center gap-1.5">
            <BarChart3 size={14} className="text-indigo-500" />
            Présence aux évaluations <span className="font-normal text-gray-400">— {statsNotes.total} étudiant(s)</span>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
            <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-700 rounded px-2 py-1.5">
              <div className="text-[10px] text-green-700 dark:text-green-300 uppercase tracking-wide font-semibold">Examen ✓</div>
              <div className="text-xl font-bold text-green-700 dark:text-green-300">{statsNotes.avec_examen}</div>
              <div className="text-[10px] text-green-600 dark:text-green-400">avec note d'examen</div>
            </div>
            <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-700 rounded px-2 py-1.5">
              <div className="text-[10px] text-red-700 dark:text-red-300 uppercase tracking-wide font-semibold">Examen ✗</div>
              <div className="text-xl font-bold text-red-700 dark:text-red-300">{statsNotes.sans_examen}</div>
              <div className="text-[10px] text-red-600 dark:text-red-400">sans note d'examen</div>
            </div>
            <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-700 rounded px-2 py-1.5">
              <div className="text-[10px] text-blue-700 dark:text-blue-300 uppercase tracking-wide font-semibold">Devoir ✓</div>
              <div className="text-xl font-bold text-blue-700 dark:text-blue-300">{statsNotes.avec_devoir}</div>
              <div className="text-[10px] text-blue-600 dark:text-blue-400">ayant fait le devoir</div>
            </div>
            <div className="bg-orange-50 dark:bg-orange-900/20 border border-orange-200 dark:border-orange-700 rounded px-2 py-1.5">
              <div className="text-[10px] text-orange-700 dark:text-orange-300 uppercase tracking-wide font-semibold">Devoir ✗</div>
              <div className="text-xl font-bold text-orange-700 dark:text-orange-300">{statsNotes.sans_devoir}</div>
              <div className="text-[10px] text-orange-600 dark:text-orange-400">n'ayant pas fait le devoir</div>
            </div>
          </div>
        </div>
      )}

      {/* Message si pas de matière sélectionnée */}
      {classe && !matiereId && (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow border border-gray-200 dark:border-gray-700 p-12 text-center">
          <BookOpen size={48} className="mx-auto text-gray-300 dark:text-gray-600 mb-3" />
          <p className="text-sm text-gray-600 dark:text-gray-300 font-medium">
            Sélectionnez une matière dans la liste de gauche
          </p>
          <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">
            pour afficher les notes des étudiants de la classe {classe}
          </p>
        </div>
      )}

      {/* Tableau */}
      {classe && matiereId && (
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow border border-gray-200 dark:border-gray-700">
          <div className="px-5 py-4 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <BarChart3 size={18} className="text-blue-500" />
              <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100">
                {selectedMatiere ? `${selectedMatiere.nom_matiere} (${selectedMatiere.code_matiere})` : 'Toutes les matières'}
                <span className="ml-2 text-sm text-gray-400 font-normal">— {classe}</span>
              </h3>
              {!isLoading && (
                <span className="ml-2 px-2 py-0.5 bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 rounded-full text-xs">
                  {filteredNotes.length} ligne(s)
                </span>
              )}
            </div>
            <div className="flex items-center gap-2">
              <span className="text-xs text-gray-400 dark:text-gray-500 flex items-center gap-1">
                <Edit3 size={12} /> Double-cliquez sur une note pour la modifier
              </span>
              <button
                onClick={refetchAll}
                className="p-2 text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg"
                title="Actualiser"
              >
                <RefreshCw size={15} />
              </button>
            </div>
          </div>

          {isLoading ? (
            <div className="flex flex-col items-center justify-center py-16">
              <Loader size={32} className="animate-spin text-blue-500 mb-3" />
              <p className="text-sm text-gray-500 dark:text-gray-400">Chargement des notes…</p>
            </div>
          ) : filteredNotes.length === 0 ? (
            <div className="py-16 text-center text-gray-400 dark:text-gray-500">
              <BarChart3 size={40} className="mx-auto mb-3 opacity-30" />
              <p className="text-sm">Aucune note saisie pour ces critères.</p>
            </div>
          ) : (
            <div className="overflow-auto max-h-[calc(100vh-280px)]">
              <table className="w-full text-xs">
                <thead className="sticky top-0 z-20">
                  <tr className="text-[10px] uppercase tracking-wide text-gray-500 dark:text-gray-400 shadow-sm">
                    <th className="px-2 py-2 text-left cursor-pointer bg-gray-50 dark:bg-gray-700" onClick={() => handleSort('matricule')}>
                      Matricule <SortIcon field="matricule" />
                    </th>
                    <th className="px-2 py-2 text-left cursor-pointer bg-gray-50 dark:bg-gray-700" onClick={() => handleSort('nom')}>
                      Nom <SortIcon field="nom" />
                    </th>
                    <th className="px-1 py-2 text-center bg-blue-100 dark:bg-blue-900/40 cursor-pointer" onClick={() => handleSort('d1')}>D1<SortIcon field="d1" /></th>
                    <th className="px-1 py-2 text-center bg-blue-100 dark:bg-blue-900/40 cursor-pointer" onClick={() => handleSort('d2')}>D2<SortIcon field="d2" /></th>
                    <th className="px-1 py-2 text-center bg-blue-100 dark:bg-blue-900/40 cursor-pointer" onClick={() => handleSort('moyenne')}>
                      Moy.D<br/><span className="text-[8px]">×0,4</span>
                    </th>
                    <th className="px-1 py-2 text-center bg-green-100 dark:bg-green-900/40 cursor-pointer" onClick={() => handleSort('so')}>SO<SortIcon field="so" /></th>
                    <th className="px-1 py-2 text-center bg-orange-100 dark:bg-orange-900/40 cursor-pointer" onClick={() => handleSort('sr')}>SR<SortIcon field="sr" /></th>
                    <th className="px-1 py-2 text-center bg-green-100 dark:bg-green-900/40">
                      Moy.Ex<br/><span className="text-[8px]">×0,6</span>
                    </th>
                    <th className="px-1 py-2 text-center bg-purple-100 dark:bg-purple-900/40">Moy.ECUE</th>
                    <th className="px-2 py-2 text-left bg-gray-50 dark:bg-gray-700">Saisi par</th>
                    <th className="px-2 py-2 text-center bg-gray-50 dark:bg-gray-700">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                  {filteredNotes.map((ligne) => {
                    // Calculs Moy.D pondérée, Moy.Ex pondérée, Moy.ECUE
                    const moyDPond = ligne.moyenneDevoirs !== null ? ligne.moyenneDevoirs * 0.4 : null;
                    const noteEx = ligne.sr !== null ? ligne.sr : ligne.so;
                    const moyExPond = noteEx !== null ? noteEx * 0.6 : null;
                    const moyEcue = (moyDPond !== null && moyExPond !== null) ? moyDPond + moyExPond : null;
                    return (
                    <tr key={`${ligne.etudiant_id}-${ligne.matiere_id}`} className="hover:bg-gray-50 dark:hover:bg-gray-700/30">
                      <td className="px-2 py-1.5 text-left font-mono text-[11px] text-gray-700 dark:text-gray-300">{ligne.etudiant_matricule}</td>
                      <td className="px-2 py-1.5 text-left font-medium text-gray-900 dark:text-gray-100">{ligne.etudiant_nom}</td>
                      <td className="px-1 py-1.5 text-center tabular-nums bg-blue-50/30 dark:bg-blue-900/5">
                        {renderEditableCell(ligne, 'd1', ligne.d1, ligne.absent1, ligne.devoirVerrouille)}
                      </td>
                      <td className="px-1 py-1.5 text-center tabular-nums bg-blue-50/30 dark:bg-blue-900/5">
                        {renderEditableCell(ligne, 'd2', ligne.d2, ligne.absent2, ligne.devoirVerrouille)}
                      </td>
                      <td className="px-1 py-1.5 text-center font-semibold tabular-nums bg-blue-50/30 dark:bg-blue-900/5">
                        {moyDPond !== null ? (
                          <span className={ligne.moyenneDevoirs! >= 10 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}>
                            {formatNote(moyDPond)}
                          </span>
                        ) : <span className="text-gray-300 dark:text-gray-600">—</span>}
                      </td>
                      <td className="px-1 py-1.5 text-center tabular-nums bg-green-50/30 dark:bg-green-900/5">
                        {renderEditableCell(ligne, 'so', ligne.so, ligne.absentSO, ligne.examenVerrouille)}
                      </td>
                      <td className="px-1 py-1.5 text-center tabular-nums bg-orange-50/30 dark:bg-orange-900/5">
                        {renderEditableCell(ligne, 'sr', ligne.sr, ligne.absentSR, ligne.examenVerrouille)}
                      </td>
                      <td className="px-1 py-1.5 text-center font-semibold tabular-nums bg-green-50/30 dark:bg-green-900/5">
                        {moyExPond !== null ? (
                          <span className={noteEx! >= 10 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}>
                            {formatNote(moyExPond)}
                          </span>
                        ) : <span className="text-gray-300 dark:text-gray-600">—</span>}
                      </td>
                      <td className="px-1 py-1.5 text-center font-bold tabular-nums bg-purple-50/30 dark:bg-purple-900/5">
                        {moyEcue !== null ? (
                          <span className={moyEcue >= 10 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}>
                            {formatNote(moyEcue)}
                          </span>
                        ) : <span className="text-gray-300 dark:text-gray-600">—</span>}
                      </td>
                      <td className="px-2 py-1.5 text-[10px] text-gray-600 dark:text-gray-400">
                        {(() => {
                          const dev = ligne.modifie_par_nom_devoir || ligne.saisi_par_nom_devoir;
                          const exa = ligne.modifie_par_nom_examen || ligne.saisi_par_nom_examen;
                          if (!dev && !exa) return <span className="text-gray-300">—</span>;
                          if (dev && exa && dev === exa) {
                            return <span title={`Saisi par ${dev}`}>{dev}</span>;
                          }
                          return (
                            <div className="flex flex-col leading-tight">
                              {dev && <span title={`Devoirs : ${dev}`} className="text-blue-700 dark:text-blue-300">D: {dev}</span>}
                              {exa && <span title={`Examens : ${exa}`} className="text-emerald-700 dark:text-emerald-300">Ex: {exa}</span>}
                            </div>
                          );
                        })()}
                      </td>
                      <td className="px-2 py-1.5 text-center">
                        <div className="flex items-center justify-center gap-1">
                          <button
                            onClick={() => setBulletinEtudiantId(ligne.etudiant_id)}
                            className="px-2 py-1 text-[10px] bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 rounded hover:bg-blue-200 flex items-center gap-1"
                            title="Bulletin de l'étudiant"
                          >
                            <FileText size={11} /> Bulletin
                          </button>
                          <button
                            onClick={() => setRachatEtudiant({
                              etudiantId: ligne.etudiant_id,
                              matricule: ligne.etudiant_matricule,
                              nom: ligne.etudiant_nom,
                              moyenneActuelle: moyEcue,
                            })}
                            className="px-2 py-1 text-[10px] bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300 rounded hover:bg-purple-200 flex items-center gap-1"
                            title="Rachat intelligent"
                          >
                            <Sparkles size={11} /> Rachat
                          </button>
                          <button
                            onClick={() => setHistoriqueLigne({
                              noteDevoirId: ligne.noteDevoir_id,
                              noteExamenId: ligne.noteExamen_id,
                              etudiantNom: ligne.etudiant_nom,
                              etudiantMatricule: ligne.etudiant_matricule,
                              matiereCode: ligne.matiere_code,
                              matiereNom: ligne.matiere_nom,
                            })}
                            disabled={!ligne.noteDevoir_id && !ligne.noteExamen_id}
                            className="p-1 text-indigo-700 dark:text-indigo-300 hover:bg-indigo-100 dark:hover:bg-indigo-900/30 rounded disabled:opacity-30 disabled:cursor-not-allowed"
                            title={ligne.noteDevoir_id || ligne.noteExamen_id ? 'Historique des modifications de cette note' : 'Aucune note saisie — pas d\'historique'}
                          >
                            <Clock size={13} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );})}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
        </div>
      </div>

      {/* Historique Panel */}
      {showHistorique && (
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow border border-gray-200 dark:border-gray-700">
          <div className="px-5 py-4 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <History size={18} className="text-amber-500" />
              <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100">
                Historique des modifications
              </h3>
              <span className="px-2 py-0.5 bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300 rounded-full text-xs">
                {historiqueFusionne.length} entrée(s)
              </span>
            </div>
            <button
              onClick={() => setShowHistorique(false)}
              className="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
            >
              <X size={18} />
            </button>
          </div>
          {historiqueFusionne.length === 0 ? (
            <div className="py-10 text-center text-gray-400 dark:text-gray-500 text-sm">
              Aucune modification enregistrée.
            </div>
          ) : (
            <div className="overflow-x-auto max-h-96 overflow-y-auto">
              <table className="min-w-full text-sm">
                <thead className="sticky top-0 bg-gray-50 dark:bg-gray-700/50">
                  <tr className="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">
                    <th className="px-4 py-2 text-left">Date</th>
                    <th className="px-4 py-2 text-left">Type</th>
                    <th className="px-4 py-2 text-left">Étudiant</th>
                    <th className="px-4 py-2 text-left">Matière</th>
                    <th className="px-4 py-2 text-left">Champ</th>
                    <th className="px-4 py-2 text-center">Ancienne</th>
                    <th className="px-4 py-2 text-center">Nouvelle</th>
                    <th className="px-4 py-2 text-left">Par</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                  {historiqueFusionne.map(h => (
                    <tr key={h.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/30">
                      <td className="px-4 py-2 text-xs text-gray-500 dark:text-gray-400 whitespace-nowrap">
                        {new Date(h.date).toLocaleString('fr-FR', { day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit' })}
                      </td>
                      <td className="px-4 py-2">
                        <span className={`px-1.5 py-0.5 rounded text-xs font-medium ${h.type === 'devoir' ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300' : 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300'}`}>
                          {h.type === 'devoir' ? 'Devoir' : 'Examen'}
                        </span>
                      </td>
                      <td className="px-4 py-2 font-mono text-xs text-gray-700 dark:text-gray-300">{h.etudiant}</td>
                      <td className="px-4 py-2 text-xs text-gray-600 dark:text-gray-400">{h.matiere}</td>
                      <td className="px-4 py-2 text-xs text-gray-600 dark:text-gray-400">{h.champ}</td>
                      <td className="px-4 py-2 text-center">
                        {h.ancien != null ? (
                          <span className="text-red-500 dark:text-red-400 text-xs line-through">{h.ancien}</span>
                        ) : <span className="text-gray-300 dark:text-gray-600 text-xs">—</span>}
                      </td>
                      <td className="px-4 py-2 text-center">
                        {h.nouveau != null ? (
                          <span className="text-green-600 dark:text-green-400 text-xs font-medium">{h.nouveau}</span>
                        ) : <span className="text-gray-300 dark:text-gray-600 text-xs">—</span>}
                      </td>
                      <td className="px-4 py-2 text-xs text-gray-600 dark:text-gray-400">{h.par || '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Quick Actions */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div
          onClick={() => navigate('/saisie-notes-devoirs')}
          className="bg-white dark:bg-gray-800 rounded-xl shadow border border-gray-200 dark:border-gray-700 p-5 hover:shadow-lg transition cursor-pointer"
        >
          <div className="flex items-center space-x-4">
            <div className="w-12 h-12 bg-blue-100 dark:bg-blue-900/20 rounded-lg flex items-center justify-center">
              <BookOpen className="text-blue-600 dark:text-blue-400" size={24} />
            </div>
            <div>
              <h3 className="font-medium text-gray-900 dark:text-gray-100">Saisie des Devoirs</h3>
              <p className="text-sm text-gray-600 dark:text-gray-400">Matricielle, individuelle ou ligne</p>
            </div>
          </div>
        </div>
        <div
          onClick={() => navigate('/saisie-notes-examens')}
          className="bg-white dark:bg-gray-800 rounded-xl shadow border border-gray-200 dark:border-gray-700 p-5 hover:shadow-lg transition cursor-pointer"
        >
          <div className="flex items-center space-x-4">
            <div className="w-12 h-12 bg-green-100 dark:bg-green-900/20 rounded-lg flex items-center justify-center">
              <BookOpen className="text-green-600 dark:text-green-400" size={24} />
            </div>
            <div>
              <h3 className="font-medium text-gray-900 dark:text-gray-100">Saisie des Examens</h3>
              <p className="text-sm text-gray-600 dark:text-gray-400">Notes d'examens par matière</p>
            </div>
          </div>
        </div>
        <div
          onClick={() => navigate('/resultats')}
          className="bg-white dark:bg-gray-800 rounded-xl shadow border border-gray-200 dark:border-gray-700 p-5 hover:shadow-lg transition cursor-pointer"
        >
          <div className="flex items-center space-x-4">
            <div className="w-12 h-12 bg-purple-100 dark:bg-purple-900/20 rounded-lg flex items-center justify-center">
              <BarChart3 className="text-purple-600 dark:text-purple-400" size={24} />
            </div>
            <div>
              <h3 className="font-medium text-gray-900 dark:text-gray-100">Résultats</h3>
              <p className="text-sm text-gray-600 dark:text-gray-400">Consulter les résultats des étudiants</p>
            </div>
          </div>
        </div>
      </div>

      {/* Modale Bulletin */}
      {bulletinEtudiantId !== null && (
        <BulletinModal
          etudiantId={bulletinEtudiantId}
          semestre={semestre === 'S1' ? 1 : 2}
          anneeAcademique={anneeAcademique}
          onClose={() => setBulletinEtudiantId(null)}
        />
      )}

      {/* Modale Rachat intelligent (étudiant sélectionné) */}
      {rachatEtudiant !== null && (
        <ModalRachatIntelligent
          etudiantId={rachatEtudiant.etudiantId}
          matricule={rachatEtudiant.matricule}
          nom={rachatEtudiant.nom}
          niveau={niveau}
          classe={classe}
          semestre={semestre === 'S1' ? 1 : 2}
          moyenneActuelle={rachatEtudiant.moyenneActuelle}
          onClose={() => setRachatEtudiant(null)}
          onSuccess={() => setRachatEtudiant(null)}
        />
      )}

      {/* Modal historique d'une ligne précise (Option A) */}
      <HistoriqueNoteModal
        open={historiqueLigne !== null}
        onClose={() => setHistoriqueLigne(null)}
        noteDevoirId={historiqueLigne?.noteDevoirId ?? null}
        noteExamenId={historiqueLigne?.noteExamenId ?? null}
        etudiantNom={historiqueLigne?.etudiantNom}
        etudiantMatricule={historiqueLigne?.etudiantMatricule}
        matiereCode={historiqueLigne?.matiereCode}
        matiereNom={historiqueLigne?.matiereNom}
      />

      {/* Modal audit global (Option B) */}
      <AuditNotesModal
        open={showAuditModal}
        onClose={() => setShowAuditModal(false)}
      />

      {/* Modal report examen→devoir si devoir=0 (sensible : preview + confirm) */}
      {matiereId && classe && (
        <ReporterExamenSiZeroModal
          open={showReportZeroModal}
          matiereId={matiereId}
          matiereLabel={
            (matieres?.find((m: any) => m.id === matiereId)?.nom_matiere as string) || ''
          }
          semestre={semestre}
          anneeAcademique={anneeAcademique}
          classe={classe}
          onClose={() => setShowReportZeroModal(false)}
          onSuccess={refetchAll}
        />
      )}

      {/* Modal report BATCH (toutes matières d'un scope donné) */}
      <ReporterExamenSiZeroBatchModal
        open={showReportZeroBatchModal}
        anneeAcademique={anneeAcademique}
        semestreDefaut={semestre}
        scopeDefaut={{
          pole: poleFilter || undefined,
          niveau: niveau || undefined,
          classe: classe || undefined,
        }}
        onClose={() => setShowReportZeroBatchModal(false)}
        onSuccess={refetchAll}
      />

      {/* Modal analyse intelligente */}
      <AnalyseIntelligenteModal
        open={analyseScenario !== null}
        onClose={() => setAnalyseScenario(null)}
        scenario={analyseScenario}
        anneeAcademique={anneeAcademique}
        semestre={semestre}
        onSuccess={refetchAll}
      />

      {/* Bouton flottant « Retour en haut » */}
      <ScrollToTop threshold={500} />
    </div>
  );
};
